import { useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import { FormatTypeEnum } from 'modules/I18N';
import { handleValueFormat } from 'components/pages/DealWithLines/common/RightSidebar/utils';
import { DealStatus, DealType } from 'components/common/types/Deal.types';
import reportSvg from 'assets/icons/report.svg';
import listSvg from 'assets/icons/list.svg';
import Button, { Color, Size } from 'lib/Button';
import { CardTheme } from 'lib/Card/Card.types';
import { LevelType } from 'consts/thoughtspot';
import { ReportType } from 'components/common/AllocationReport/AllocationReport.types';
import usePendoTracking from 'customHooks/usePendoTracking';
import { AllocationFrameList } from 'components/common/Deal/AllocationFrameList/AllocationFrameList';
import { getCurrencyCode, getLocaleCode } from 'store/publisher/selectors';
import { getCurrentLineBookingStatusCode } from 'store/dealManagement/selectors/getCurrentLineBookingStatusCode';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { getIsNewDeal } from 'store/dealManagement/selectors/getIsNewDeal';
import { DealSummaryCard } from '../../DealSummary/DealSummaryCard/DealSummaryCard';
import DealSummaryCardSection from '../../DealSummary/DealSummaryCardSection';
import { DealSummaryCardSectionLineType } from '../../DealSummary/DealSummaryCardSection/DealSummaryCardSection.types';
import { DealSummaryAllocationReport } from '../../DealSummaryAllocationReport/DealSummaryAllocationReport';
import { AllocationCardProps } from './AllocationCard.types';
import { getDealSummaryCardVisibility } from '../selectors';

const AllocationCard: React.FC<AllocationCardProps> = ({
  allocatedFrames,
  allocatedImpressions,
  assets,
  defaultAllocationReportLevelType = LevelType.LINE,
  totalCost,
}) => {
  const { getPendoDataId } = usePendoTracking();
  const currencyCode = useSelector(getCurrencyCode);
  const localeCode = useSelector(getLocaleCode);
  const isForecastedAllocation = useSelector((state: Store) => state.dealManagement.isForecastedAllocation);
  const isNewDeal = useSelector(getIsNewDeal);
  const dealType = useSelector(getDealType);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const { isAllocationCardVisible } = useSelector(getDealSummaryCardVisibility);
  const currentLinebookingStatusCode = useSelector(getCurrentLineBookingStatusCode);
  const isGuaranteed = dealType === DealType.GUARANTEED;

  const allocationCardTheme =
    dealType === DealType.GUARANTEED &&
    (isNewDeal || bookingStatusCode === DealStatus.APPROVED || isForecastedAllocation)
      ? CardTheme.PRIMARY
      : CardTheme.DEFAULT;

  const isReadOnly =
    isGuaranteed &&
    currentLinebookingStatusCode &&
    [DealStatus.TERMINATED, DealStatus.ENDED].includes(currentLinebookingStatusCode);

  const buttonsVisibility = {
    frameList: defaultAllocationReportLevelType === LevelType.LINE,
    report: isGuaranteed && bookingStatusCode !== DealStatus.CANCELLED,
  };
  const showButtonsContainer = Object.values(buttonsVisibility).some((isButtonVisible) => isButtonVisible);

  return (
    <DealSummaryCard header="ALLOCATION" isVisible={isAllocationCardVisible} theme={allocationCardTheme}>
      <DealSummaryCardSection
        lines={[
          {
            label: currencyCode,
            value: handleValueFormat({
              valueType: FormatTypeEnum.NUMBER,
              value: totalCost,
              currencyCode,
              localeCode,
            }),
            lineType: DealSummaryCardSectionLineType.HEADER,
          },
        ]}
        isReadOnly={isReadOnly}
        id="allocation-price"
      />
      <DealSummaryCardSection
        lines={[
          {
            label: 'Impressions',
            value: handleValueFormat({
              valueType: FormatTypeEnum.ROUNDED_NUMBER,
              value: allocatedImpressions,
              currencyCode,
              localeCode,
            }),
            lineType: DealSummaryCardSectionLineType.HEADER,
          },
        ]}
        isReadOnly={isReadOnly}
        id="allocation-impressions"
      />
      <DealSummaryCardSection
        lines={[
          {
            label: 'Frames',
            value: handleValueFormat({
              valueType: FormatTypeEnum.ROUNDED_NUMBER,
              value: allocatedFrames,
              currencyCode,
              localeCode,
            }),
            lineType: DealSummaryCardSectionLineType.HEADER,
          },
        ]}
        isReadOnly={isReadOnly}
        id="allocation-frames"
      >
        {showButtonsContainer && (
          <div className="flex flex-row gap-x-2 h-9">
            {buttonsVisibility.frameList && !!assets?.length && (
              <AllocationFrameList
                assets={assets}
                actionButton={(onClick) => (
                  <Button color={Color.TERTIARY} label="Frame list" size={Size.WIDE} svg={listSvg} onClick={onClick} />
                )}
              />
            )}
            {buttonsVisibility.report && (
              <DealSummaryAllocationReport
                buttonLabel="Report"
                actionButton={(onClick) => (
                  <Button
                    color={Color.TERTIARY}
                    label="Report"
                    size={Size.WIDE}
                    svg={reportSvg}
                    onClick={onClick}
                    ddActionName="Allocation report / Allocation card report button"
                    dataPendoId={getPendoDataId('Allocation report button')}
                  />
                )}
                reportType={ReportType.SAVED}
                defaultLevelType={defaultAllocationReportLevelType}
              />
            )}
          </div>
        )}
      </DealSummaryCardSection>
    </DealSummaryCard>
  );
};

export default AllocationCard;
